import { useRollbarPerson } from "@rollbar/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { usePostHog } from "posthog-js/react"
import { useContext, useEffect, useState } from "react"
import { Navigate, useNavigate } from "react-router"

import LoadingScreen from "@sap/components/loading-screen"
import { isValidToken } from "@sap/utils/jwt"

import { getAccessToken, getCurrentUser } from "@/api"
import { ENVIRONMENT, MAIN_DOMAIN, ORIGINALS_DOMAIN, REQUIRED_GROUPS } from "@/config"
import { DomainContext } from "@/routes/providers/originals"
import { useAuthStore } from "@/store/auth"

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [isInitialized, setisInitialized] = useState(false)
  const { accessToken, refreshToken, setAccessToken } = useAuthStore((state) => state)

  const validateToken = (token: string) => {
    try {
      return isValidToken(token)
    } catch {
      navigate("/auth/login")
    }
  }

  useEffect(() => {
    if (!refreshToken || !validateToken(refreshToken)) {
      navigate("/auth/login")
    } else if (!accessToken || !validateToken(accessToken)) {
      queryClient
        .fetchQuery({
          queryKey: ["accessToken", refreshToken],
          queryFn: () => getAccessToken(refreshToken),
          staleTime: 10000,
        })
        .then((res) => {
          setAccessToken(res.data.access)
          setisInitialized(true)
        })
        .catch(() => {
          navigate("/auth/login")
        })
    } else {
      setisInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken, accessToken])

  if (!refreshToken || !validateToken(refreshToken)) {
    return <Navigate to="/auth/login" />
  }

  if ((!accessToken || !validateToken(accessToken)) && !isInitialized) {
    return <LoadingScreen isDashboard />
  }

  return <UserGuard>{children}</UserGuard>
}

function UserGuard({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore((state) => state)
  const posthog = usePostHog()
  const { isOriginals, port } = useContext(DomainContext)

  useRollbarPerson(user || {})

  const { isFetching } = useQuery({
    queryKey: ["currentUser"],
    queryFn: getCurrentUser,
    enabled: true,
    throwOnError: true,
    ...(user && { placeholderData: user }),
  })

  useEffect(() => {
    if (user?.id) {
      posthog.identify(String(user.id), {
        email: user.email,
        environment: ENVIRONMENT,
        is_staff: user.is_staff,
        is_pl_originals: user.is_pl_originals,
        is_pl_external: user.is_pl_external,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  if (!isOriginals && !isFetching && user?.is_pl_originals) {
    console.log("Redirecting to Originals")
    if (import.meta.env.PROD) {
      window.location.replace(
        `https://${ORIGINALS_DOMAIN}${port && `:${port}`}/auth/logout?redirect=true`
      )
    }
  }

  if (isOriginals && !isFetching && !user?.is_pl_originals) {
    console.log("Redirecting to Main")
    if (import.meta.env.PROD) {
      window.location.replace(
        `https://${MAIN_DOMAIN}${port && `:${port}`}/auth/logout?redirect=true`
      )
    }
  }

  const isAuthorized = user?.groups.some((group) => REQUIRED_GROUPS.includes(group))

  if (!user) {
    return <LoadingScreen key="UserGaurd" isDashboard />
  }

  if (!isAuthorized && isFetching) {
    return <LoadingScreen key="UserGaurd" isDashboard />
  }

  if (!isAuthorized) {
    return <Navigate to="/401" />
  }

  return <>{children}</>
}
